import React, { useState, useEffect, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import Numeral from "../../../components/numeral/Numeral";
import { Tertiary } from "../../../components/buttons/Tertiary";
import { HttpRequest } from "../../../services/httpRequest";
import { SkeletonDahsboard } from "../../../components/skeleton/skeletonDash";
import { validateSessionAndJwtToken } from "../../../utils/validateTokenSession";
import { returnToSignin } from "../../../utils/returnToSign";
import { DashboardMyCredit } from "../../../components/backgrounds/DashboardMyCredit";
import { UpdateData } from "../../../components/navigation/disclosure/Update/UpdateData";
import { useCredit } from "../../../context/creditContext";
import { determinaCreditoAMostrar, loadCredits } from "../credits/utils";
import { BancoPageService } from "./bancoPage.service";
import { EyeSlashIcon } from "@heroicons/react/24/outline";
import { EyeIcon } from "lucide-react";
import { homologateBankNameAccount } from "../../../utils/options_selects";

const bancoPageService = new BancoPageService();

export function ProfilePage() {
  let navigate = useNavigate();
  const { disclosures, creditInfo, due } = useCredit();
  const [basicInfo, setBasicInfo] = useState({});
  const [creditsInfo, setCreditsInfo] = useState([]);
  const [, setUserInfoAuth] = useState({});
  const [features, setFeatures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [numAccountsArray, setNumAccountsArray] = useState([]);
  const [accountBooleanShowIcon, setAccountBooleanShowIcon] = useState([]);
  const [numAccountsArrayHide, setNumAccountsArrayHide] = useState([]);
  const [numAccountsArrayCompleted, setNumAccountsArrayCompleted] = useState(
    [],
  );
  const loadCreditById = useCallback(async () => {
    try {
      const data = await HttpRequest.getCreditById(
        localStorage.getItem("credit_id"),
      );
      return data.message.info;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const handleToggle = (index) => {
    if (accountBooleanShowIcon[index]) {
      const updatedArray = [...numAccountsArrayHide];
      updatedArray.splice(index, 1, numAccountsArrayHide[index]);
      setNumAccountsArray(updatedArray);
    } else {
      const updatedArray = [...numAccountsArrayHide];
      updatedArray.splice(index, 1, numAccountsArrayCompleted[index]);
      setNumAccountsArray(updatedArray);
    }

    const ArrayShowIcon = [...accountBooleanShowIcon];
    ArrayShowIcon.splice(index, 1, !accountBooleanShowIcon[index]);
    setAccountBooleanShowIcon(ArrayShowIcon);
  };

  const loadInfoUser = useCallback(async () => {
    try {
      const data = await HttpRequest.getInfoUser(localStorage.getItem("doc"));
      return data.message.info;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const loadInfoUserAuth = useCallback(async () => {
    try {
      const data = await HttpRequest.getUserAuth(localStorage.getItem("doc"));
      return data.message.user;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const configData = useCallback((basicInfoU, userAuth) => {
    const basicInfo = basicInfoU.myBankAccounts.filter((myBankAccount) => myBankAccount.parent === true)[0]
    let features = [
      {
        title: "Datos de contacto",
        classBox:
          "col-span-8 md:col-span-4 grid content-between h-full rounded-2xl transition pb-4 pt-8 lg:pt-4 px-8 bg-white",
        details: [
          {
            name: "WhatsApp:",
            value: `+57 ${userAuth.phone}`,
            id: 2,
          },
          {
            name: "Correo electrónico:",
            value: userAuth.email,
            id: 3,
          },
        ],
        button: <UpdateData />,
      },
      {
        title: "Cuenta bancaria",
        href: "/mis-bancos",
        classBox:
          "col-span-8 mt-6 md:col-span-4 grid content-between h-full rounded-2xl transition pb-4 pt-8 lg:pt-4 px-8 bg-white",
        details: [
          {
            name: "Banco:",
            value: basicInfo.bankAccount,
            id: 0,
          },
          {
            name: "Tipo de cuenta:",
            value: basicInfo.typeBankAccount,
            id: 1,
          },
          {
            name: "Número de cuenta:",
            value: basicInfo.accountNumber,
            id: 2,
            iconShow: <EyeIcon key={2} width={15} height={15} />,
            iconHide: <EyeSlashIcon key={2} width={15} height={15} />,
          },
        ],
        button: "Cambiar cuenta",
      },
    ];

    setFeatures(features);
    return;
  }, []);

  const determineIfCreditIsPendingToSign = useCallback(async () => {
    try {
      let credits = await loadCredits();
      let currentCredit = determinaCreditoAMostrar(credits);
      if (currentCredit[0]?.status === "Pendiente de firmar") {
        return navigate("/request-credit?renovar=true");
      }
    } catch (error) {
      console.log(error);
      return;
    }
  }, [navigate]);

  const refreshAccounts = async () => {
    let _accounts = await bancoPageService.loadAccounts();
    for (let i = 0; i < _accounts.length; i++) {
      const element = _accounts[i];
      if (element.parent === true) {
        bancoPageService.mapAccounts([element], {
          setNumAccountsArray,
          setNumAccountsArrayHide,
          setNumAccountsArrayCompleted,
          setFeatures,
          setAccountBooleanShowIcon,
        });
      }
    }
  };

  useEffect(() => {
    refreshAccounts();
  }, []);

  useEffect(() => {
    const validSessionAndTokenJwt = async () => {
      try {
        let isValidToken = await validateSessionAndJwtToken();
        return isValidToken;
      } catch (error) {
        return false;
      }
    };

    const loadInfo = async () => {
      let info = await loadInfoUser();
      return info;
    };

    const loadUserAuth = async () => {
      let userAuth = await loadInfoUserAuth();
      return userAuth;
    };

    const _loadCreditById = async () => {
      let credit = await loadCreditById();
      return credit;
    };

    validSessionAndTokenJwt()
      .then(async (isValidToken) => {
        if (isValidToken) {
          try {
            await determineIfCreditIsPendingToSign();
            _loadCreditById()
              .then(async (creditData) => {
                setCreditsInfo(creditData);
              })
              .catch((e) => console.log(e));
            let info = await loadInfo();
            let userAuth = await loadUserAuth();
            setBasicInfo(info);
            setIsLoading(false);
            setUserInfoAuth(userAuth);
            configData(info, userAuth);
          } catch (error) {
            console.log(error);
            localStorage.removeItem("doc");
            localStorage.setItem("is_signin", false);
            return navigate("/ingresar");
          }
        } else {
          returnToSignin(navigate);
        }
      })
      .catch((e) => {
        console.log(e);
        returnToSignin(navigate);
      });
  }, [
    navigate,
    loadInfoUser,
    loadInfoUserAuth,
    loadCreditById,
    determineIfCreditIsPendingToSign,
    configData,
  ]);

  if (isLoading) {
    return (
      <Fragment>
        <DashboardMyCredit
          name={"Cargando"}
          balance={""}
          current={"4"}
          title={"Perfil"}
          main={
            <>
              <div className="pb-6 mt-10 mb-32 md:mb-0 mx-6 lg:mx-0">
                {/* Cards Profile */}
                <div className="col-span-8 mt-6 md:col-span-4 grid content-between h-full rounded-2xl transition pb-4 pt-8 lg:pt-4 px-8 bg-white">
                  <div className="flex justify-between">
                    <SkeletonDahsboard height={20} width={130} />
                  </div>
                  <div className="pt-2">
                    <div className="py-2 md:py-0 md:grid grid-cols-8">
                      <div className="col-span-4">
                        <SkeletonDahsboard height={15} width={80} />
                      </div>
                      <div className="col-span-4">
                        <SkeletonDahsboard height={30} width={140} />
                      </div>
                    </div>
                    <div className="py-2 md:py-0 md:grid grid-cols-8">
                      <div className="col-span-4">
                        <SkeletonDahsboard height={15} width={120} />
                      </div>
                      <div className="col-span-4">
                        <SkeletonDahsboard height={30} width={100} />
                      </div>
                    </div>
                    <div className="py-2 md:py-0 md:grid grid-cols-8">
                      <div className="col-span-4">
                        <SkeletonDahsboard height={15} width={140} />
                      </div>
                      <div className="col-span-4">
                        <SkeletonDahsboard height={30} width={140} />
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end text-grayP">
                    <SkeletonDahsboard height={15} width={60} />
                  </div>
                </div>
                <div className="col-span-8 mt-6 md:col-span-4 grid content-between h-full rounded-2xl transition pb-4 pt-8 lg:pt-4 px-8 bg-white">
                  <div className="flex justify-between">
                    <SkeletonDahsboard height={20} width={110} />
                  </div>
                  <div className="pt-2">
                    <div className="py-2 md:py-0 md:grid grid-cols-8">
                      <div className="col-span-4">
                        <SkeletonDahsboard height={15} width={80} />
                      </div>
                      <div className="col-span-4">
                        <SkeletonDahsboard height={30} width={110} />
                      </div>
                    </div>
                    <div className="py-2 md:py-0 md:grid grid-cols-8">
                      <div className="col-span-4">
                        <SkeletonDahsboard height={15} width={120} />
                      </div>
                      <div className="col-span-4">
                        <SkeletonDahsboard height={30} width={80} />
                      </div>
                    </div>
                    <div className="py-2 md:py-0 md:grid grid-cols-8">
                      <div className="col-span-4">
                        <SkeletonDahsboard height={15} width={110} />
                      </div>
                      <div className="col-span-4">
                        <SkeletonDahsboard height={30} width={100} />
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end text-grayP">
                    <SkeletonDahsboard height={15} width={60} />
                  </div>
                </div>
              </div>
            </>
          }
        />
      </Fragment>
    );
  }

  return (
    <DashboardMyCredit
      name={basicInfo.firstname}
      balance={
        creditsInfo?.status === "Cancelado" ? 0 : creditsInfo?.paymentBalance
      }
      current={"4"}
      title={"Perfil"}
      disclosures={disclosures}
      creditInfo={creditInfo}
      due={due}
      main={
        <>
          <div className="pb-6 mt-10 mb-32 md:mb-0 mx-6 lg:mx-0">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className={
                  feature.classBox ||
                  "rounded-2xl transition shadow-lg p-4 bg-white hover:bg-red hover:text-white"
                }
              >
                <div key={idx} className="flex justify-between">
                  <h3 className="font-bold">{feature.title}</h3>
                  {feature.title === "Cuenta bancaria" && (
                    <div
                      className={
                        "rounded-full flex justify-center items-center"
                      }
                      style={{
                        width: "30px",
                        height: "30px",
                        padding:
                          feature.details[0]?.value === "Bancolombia"
                            ? "3px"
                            : "0px",
                        backgroundColor:
                          feature.details[0]?.value === "Bancolombia"
                            ? "black"
                            : "transparent",
                      }}
                    >
                      {homologateBankNameAccount(
                        feature.details[0]?.value,
                        "icon",
                      )}
                    </div>
                  )}
                </div>
                <div className="pt-2">
                  {feature.details.map((detail, index) => (
                    <div key={detail.id}>
                      <div className="py-2 md:py-0 md:grid grid-cols-8">
                        <span className="col-span-4 text-sm text-grayP">
                          {detail.name}{" "}
                        </span>
                        <div
                          className="col-span-4 text-xl md:text-base"
                          style={{
                            textTransform: detail.name.includes(
                              "Tipo de cuenta",
                            )
                              ? "capitalize"
                              : "",
                            letterSpacing: detail.name.includes(
                              "Banco",
                              "Tipo de cuenta",
                              "Número",
                            )
                              ? "1px"
                              : "",
                          }}
                        >
                          {detail.name.includes("Número") ? (
                            <div className="flex items-center gap-2">
                              <div>{numAccountsArray[0]}</div>
                              <div
                                key={0}
                                className="eyeicon-account"
                                onClick={() => handleToggle(0)}
                              >
                                {accountBooleanShowIcon[0]
                                  ? detail.iconHide
                                  : detail.iconShow}
                              </div>
                            </div>
                          ) : (
                            <>
                              {detail.value}{" "}
                              <Numeral
                                format={"$0,0"}
                                value={detail.number}
                              />{" "}
                            </>
                          )}
                        </div>
                      </div>
                      <div
                        key={detail.id}
                        className="text-xl mx-auto uppercase text-center"
                      >
                        <div>{detail.alert}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-end text-grayP" state={{
                  name: basicInfo.firstname,
                  balance:
                    creditsInfo?.status === "Cancelado"
                      ? 0
                      : creditsInfo?.paymentBalance,
                }}>
                  <Tertiary
                    href={feature.href}
                    text={feature.button} />
                </div>
              </div>
            ))}
          </div>
        </>
      }
    />
  );
}
